import { useMemo } from 'react';
import {
    INumericalValue,
    IValueTrend,
    TrendDirection,
    ValueSize,
} from './NumericalTile';
import { useTranslation } from 'react-i18next';

type NullableNumber = number | null;

const defaultValueFormatter = (val: number) => val.toString();

export interface IFormattingOptions {
    valueFormatter?: (val: number) => string;
    size?: ValueSize;
    showValueTooltip?: boolean;
}

export interface INumericalMetricsValue {
    label?: string;
    labelTooltip?: string;
    previous?: number | null;
    current?: number | null;
}

const getAbsTrend = (
    trend: number
): { value: number; direction: TrendDirection } => {
    if (trend === 0) {
        return {
            value: trend,
            direction: 'const',
        };
    }

    return trend > 0
        ? { value: trend, direction: 'up' }
        : { value: Math.abs(trend), direction: 'down' };
};

const formatTrendPercentage = (trend: number): IValueTrend => {
    const absTrend = getAbsTrend(trend);

    return {
        value: `${absTrend.value.toFixed(0)}%`,
        direction: absTrend.direction,
    };
};

const getFormattedTrend = (
    previousValue?: NullableNumber,
    currentValue?: NullableNumber
): IValueTrend | undefined => {
    if (
        previousValue === undefined ||
        previousValue === null ||
        isNaN(previousValue) ||
        currentValue === undefined ||
        currentValue === null ||
        isNaN(currentValue)
    ) {
        return undefined;
    }

    if (previousValue === currentValue) {
        return { direction: 'const' };
    }

    if (previousValue === 0) {
        return currentValue > 0
            ? {
                  direction: 'up',
              }
            : {
                  direction: 'down',
              };
    }

    if (currentValue === 0) {
        return previousValue > 0
            ? {
                  direction: 'down',
              }
            : {
                  direction: 'up',
              };
    }

    const percentage = (100 * (currentValue - previousValue)) / previousValue;
    return formatTrendPercentage(percentage);
};

const formatTooltip = (val?: NullableNumber) => {
    if (val === undefined || val === null || isNaN(val)) {
        return undefined;
    }

    return Intl.NumberFormat('en-GB').format(val);
};

export const useFormattedMetricsValues = (
    values: INumericalMetricsValue[],
    options?: IFormattingOptions
): INumericalValue[] => {
    const { t } = useTranslation();

    options = options ?? {};

    const valueFormatter = options.valueFormatter ?? defaultValueFormatter;
    const size = options.size || 'normal';
    const showTooltip = options.showValueTooltip || false;

    return useMemo(() => {
        const formatValue = (val?: number | null) =>
            val !== undefined && val !== null
                ? valueFormatter(val)
                : t('ReportingDashboard_NoDataLabel');

        return values.map((x) => {
            const formattedTrend = getFormattedTrend(x.previous, x.current);

            const val: INumericalValue = {
                label: x.label,
                labelTooltip: x.labelTooltip,
                previousValue: formatValue(x.previous),
                currentValue: formatValue(x.current),
                previousTooltip: showTooltip
                    ? formatTooltip(x.previous)
                    : undefined,
                currentTooltip: showTooltip
                    ? formatTooltip(x.current)
                    : undefined,
                trend: formattedTrend,
                size,
            };

            return val;
        });
    }, [values, size, showTooltip, valueFormatter, t]);
};
